class VgApiClient {

	ajaxEndpoint;
	#action;
	#params;

    constructor(country = 'es', lang = 'es') {
        this.ajaxEndpoint = `/${country}-${lang}/wp-content/plugins/vivagym-joining/includes/ajax-light.php`;
		this.#action = {};
		this.#params = {};
        this.signal = null;
    }

	set ajaxEndpoint(newEndpoint) {
		this.ajaxEndpoint = newEndpoint;
	}

	get ajaxEndpoint() {
		return this.ajaxEndpoint;
	}

	set action(newAction) {
		this.#action = newAction;
	}

	get action() {
		return this.#action;
	}

	set params(newParams) {
		this.#params = newParams;
	}

	get params() {
		return this.#params;
	}

	async handleRequest() {
		const startTime = performance.now(); // TODO: debug purposes, delete after testing
		try {
			const response = await fetch(this.ajaxEndpoint, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({
					action: this.#action,
					params: this.#params,
				}),
                signal: this.signal
			});

			if (!response.ok) {
				throw new Error(`HTTP error, Status: ${response.status}`);
			}

			const data = await response.json();

			// TODO: debug purposes, delete after testing
            const endTime = performance.now();
            const elapsedTime = endTime - startTime;
            console.log(`AJAX Total time (${this.action}): ${elapsedTime.toFixed(2)} ms`);

			return data;

		} catch (error) {
			if (error.name === 'AbortError') {
				console.error('Fetch aborted');
			} else {
				console.error("Error in AJAX request:", error);
			}
			return null;
		}
	}

	async setTablet() {
		this.action = "set_tablet";
		this.params = {};
		return this.handleRequest();
	}

	async getUserToken() {
		this.action = "get_user_token";
		this.params = {};
		return this.handleRequest();
	}

	async getUserAnonymousToken(country, lang) {
		this.action = "get_user_anonymous_token";
		this.params = { country: country, lang: lang };
		return this.handleRequest();
	}

	async validateUserSSOToken(externalId, ssoToken, country) {
		this.action = "validate_user_sso_token";
		this.params = { external_id : externalId, sso_token : ssoToken, country: country };
		return this.handleRequest();
	}

	async getUserExternalId(userToken, country) {
		this.action = "get_user_external_id";
		this.params = { user_token: userToken, country: country };
		return this.handleRequest();
	}

	async checkIfExist(email, country) {
		this.action = "check_if_exists";
		this.params = { email: email, locale: country };
		return this.handleRequest();
	}

	async getGymById(gymId) {
		this.action = "get_gym";
		this.params = {gym_id : gymId};
		return this.handleRequest();
	}

	async getGymsByRegion(country, locale) {
		this.action = "get_gyms_by_region";
		this.params = { country: country, locale: locale };
		return this.handleRequest();
	}

	async getGymsByRegionFromDatabase(country) {
		this.action = "get_gyms_by_region_from_database";
		this.params = { country: country };
		return this.handleRequest();
	}

	async getSubscriptions(gymId, country) {
		this.action = "get_subscriptions";
		this.params = { center_id: gymId, country: country };
		return this.handleRequest();
	}

	async getSubscriptionsGymsByRegion(gymId) {
		this.action = "get_subscriptions_gyms_by_region";
		this.params = { center_id: gymId};
		return this.handleRequest();
	}

	async createUser(gymId, data, country) {
		this.action = "create_user";
		this.params = { gym_id: gymId, data: data, country: country };
		return this.handleRequest();
	}

	async createUserLead(name, email, gymId, country) {
		this.action = "create_user_lead";
		this.params = { name: name, email: email, gym_id: gymId, country: country };
		return this.handleRequest();
	}

	async cleanUserLead(country) {
		this.action = "clean_user_lead";
		this.params = { country: country };
		return this.handleRequest();
	}

	async getGymSurvey(gymId, country) {
		this.action = "get_survey_by_gym_id";
		this.params = { gym_id: gymId, country: country };
		return this.handleRequest();
	}

	async updateUserSurveys(surveyData, country) {
		this.action = "update_user_surveys";
		this.params = { survey_data: surveyData, country: country };
		return this.handleRequest();
	}

	async updateUserCommunications(userPreferences, country) {
		this.action = "update_user_communications";
		this.params = { user_preferences: userPreferences, country: country };
		return this.handleRequest();
	}

	async updateUser(data, country) {
		this.action = "update_vg_user_and_password";
		this.params = { data: data, country: country };
		return this.handleRequest();
	}

	async updateUserExtendedAttribute(attributeId, value, country) {
		this.action = "update_user_extended_attribute";
		this.params = { attribute_id: attributeId, value: value, country: country };
		return this.handleRequest();
	}

	async updateUserMultipleExtendedAttribute(extendedAttributes, country) {
		this.action = "update_user_multiple_extended_attribute";
		this.params = { extended_attributes: extendedAttributes, country: country };
		return this.handleRequest();
	}

	async checkPersonNationalId(idCardNumber, locale) {
		this.action = "validate_person_national_id";
		this.params = { national_id: idCardNumber.toUpperCase(), locale: locale };
		return this.handleRequest();
	}

	async checkPersonPassport(passportNumber, country, locale) {
		this.action = "validate_person_passport";
		this.params = { passport_number: passportNumber.toUpperCase(), passport_country: country, locale: locale };
		return this.handleRequest();
	}

	async checkPostalCode(postalCode, country) {
		this.action = "validate_postal_code";
		this.params = { postal_code: postalCode, country: country };
		return this.handleRequest();
	}

	async getBasketExternalId(shoppingBasketId) {
		this.action = "get_shopping_basket";
		this.params = { shopping_basket_id: shoppingBasketId };
		return this.handleRequest();
	}

	async checkIfIsAnonymous(externalId) {
		this.action = "check_if_is_anonymous";
		this.params = { external_id: externalId };
		return this.handleRequest();
	}

	async getPaymentAgreement(country) {
		this.action = "get_payment_agreement";
		this.params = { country: country };
		return this.handleRequest();
	}

	async setPaymentAgreementSepa(sepaData, country) {
		this.action = "set_payment_agreement_sepa";
		this.params = { sepa_data: sepaData, country: country };
		return this.handleRequest();
	}

	async payShoppingBasket(basketParams, country) {
		this.action = "pay_shopping_basket";
		this.params = { basket_params: basketParams, country: country };
		return this.handleRequest();
	}

	async validatePromoCode(gymId, promoCode, country, skipAuth = false) {
		this.action = "validate_promo_code";
		this.params = { gym_id: gymId, promo_code: promoCode, country: country, skip_auth : skipAuth };
		return this.handleRequest();
	}

	async validateIban(iban, country) {
		this.action = "validate_iban";
		this.params = { iban: iban, country: country };
		return this.handleRequest();
	}

	async getAdyenSession(adyenSessionParams) {
		this.action = "get_adyen_session_no_short";
		this.params = { adyen_params: adyenSessionParams };
		return this.handleRequest();
	}
}
